import { ApiClientError, apiRequest } from "@/services/http/client";
import { completeOutboxRecord, getDeviceId, offlineDb } from "@/offline/db";
import { preserveConflict, retryBackoffSeconds } from "@/offline/stateMachine";
import type { OfflineDraft, OfflineMediaRecord, OutboxRequest } from "@/types/offline";

export interface QueueMutationInput<T> {
  entityType: string;
  entityId?: string;
  method: OutboxRequest["method"];
  path: string;
  payload: T;
  baseVersion?: number;
}

export const createOfflineDraft = async <T>(entityType: string, payload: T, entityId: string = crypto.randomUUID(), baseVersion?: number): Promise<OfflineDraft<T>> => {
  const now = new Date().toISOString();
  return offlineDb.saveDraft({ id: crypto.randomUUID(), deviceId: getDeviceId(), entityType, entityId, payload, baseVersion, state: "DRAFT", createdAt: now, updatedAt: now, capturedAt: now });
};

export const queueMutation = async <T>(input: QueueMutationInput<T>): Promise<{ draft: OfflineDraft<T>; outbox: OutboxRequest<T> }> => {
  const draft = await createOfflineDraft(input.entityType, input.payload, input.entityId, input.baseVersion);
  const now = new Date().toISOString();
  const outbox: OutboxRequest<T> = {
    id: crypto.randomUUID(), draftId: draft.id, entityType: input.entityType, entityId: draft.entityId,
    idempotencyKey: draft.entityId, method: input.method, path: input.path, payload: input.payload,
    attempts: 0, state: "QUEUED", createdAt: now,
  };
  await offlineDb.saveDraft({ ...draft, state: "QUEUED", updatedAt: now });
  await offlineDb.saveOutbox(outbox);
  window.dispatchEvent(new CustomEvent("aczon:offline-state-change"));
  return { draft: { ...draft, state: "QUEUED", updatedAt: now }, outbox };
};

export interface QueueMediaUploadInput {
  id?: string;
  entityType: string;
  entityId: string;
  path: string;
  file: File | Blob;
  fileName?: string;
  mimeType?: string;
  query: Record<string, string | number | boolean | undefined>;
}

export const queueMediaUpload = async (input: QueueMediaUploadInput): Promise<OfflineMediaRecord> => {
  const id = input.id || crypto.randomUUID();
  const now = new Date().toISOString();
  const record: OfflineMediaRecord = {
    id,
    entityType: input.entityType,
    entityId: input.entityId,
    path: input.path,
    fileName: input.fileName || (input.file instanceof File ? input.file.name : `evidence-${id}.jpg`),
    mimeType: input.mimeType || input.file.type || "application/octet-stream",
    blob: input.file,
    query: input.query,
    attempts: 0,
    state: "QUEUED",
    createdAt: now,
  };
  await offlineDb.saveMediaOutbox(record);
  window.dispatchEvent(new CustomEvent("aczon:offline-state-change"));
  return record;
};

export interface SyncOutcome { synced: number; failed: number; conflicts: number; }

let activeSync: Promise<SyncOutcome> | undefined;

const processOutboxBatch = async (phase: "PRE_MEDIA" | "POST_MEDIA" = "PRE_MEDIA"): Promise<SyncOutcome> => {
  const items = await offlineDb.listOutbox();
  const outcome: SyncOutcome = { synced: 0, failed: 0, conflicts: 0 };
  const now = Date.now();
  const ready = items
    .filter((record) => record.state !== "CONFLICT")
    .filter((record) => record.state !== "FAILED" || !record.nextAttemptAt || Date.parse(record.nextAttemptAt) <= now)
    .filter((record) => phase === "POST_MEDIA" ? record.path.endsWith("/complete") : !record.path.endsWith("/complete"))
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  for (const item of ready) {
    const attempt = { ...item, state: "PROCESSING" as const, attempts: item.attempts + 1, lastAttemptAt: new Date().toISOString() };
    await offlineDb.saveOutbox(attempt);
    try {
      await apiRequest(item.path, { method: item.method, body: JSON.stringify(item.payload), idempotencyKey: item.idempotencyKey });
      const draft = await offlineDb.getDraft(item.draftId);
      if (draft) {
        await completeOutboxRecord(
          { ...draft, state: "SYNCED", updatedAt: new Date().toISOString(), syncedAt: new Date().toISOString(), lastError: undefined },
          item.id,
        );
      } else {
        await offlineDb.removeOutbox(item.id);
      }
      outcome.synced += 1;
    } catch (reason) {
      const error = reason instanceof ApiClientError ? reason : new ApiClientError(0, { code: "UNKNOWN", message: String(reason) });
      const draft = await offlineDb.getDraft(item.draftId);
      if (error.status === 409) {
        const conflictDetails = error.payload.details as { current_version?: number; server_snapshot?: unknown } | undefined;
        const conflict = preserveConflict({ draftId: item.draftId, entityType: item.entityType, entityId: item.entityId, localPayload: item.payload, serverPayload: conflictDetails?.server_snapshot ?? error.payload.details, baseVersion: draft?.baseVersion, serverVersion: conflictDetails?.current_version });
        await offlineDb.saveConflict(conflict);
        await offlineDb.saveOutbox({ ...attempt, state: "CONFLICT", lastError: error.message });
        if (draft) await offlineDb.saveDraft({ ...draft, state: "CONFLICT", updatedAt: new Date().toISOString(), lastError: error.message, serverSnapshot: error.payload.details });
        outcome.conflicts += 1;
      } else {
        const backoffSeconds = retryBackoffSeconds(attempt.attempts);
        const nextAttemptAt = new Date(Date.now() + backoffSeconds * 1000).toISOString();
        await offlineDb.saveOutbox({ ...attempt, state: "FAILED", lastError: error.message, nextAttemptAt });
        if (draft) await offlineDb.saveDraft({ ...draft, state: "FAILED", updatedAt: new Date().toISOString(), lastError: error.message });
        outcome.failed += 1;
      }
    }
  }
  window.dispatchEvent(new CustomEvent("aczon:offline-state-change"));
  return outcome;
};

const processMediaOutboxBatch = async (): Promise<SyncOutcome> => {
  const items = await offlineDb.listMediaOutbox();
  const outcome: SyncOutcome = { synced: 0, failed: 0, conflicts: 0 };
  const now = Date.now();
  const ready = items
    .filter((record) => record.state !== "FAILED" || !record.nextAttemptAt || Date.parse(record.nextAttemptAt) <= now)
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt));

  for (const item of ready) {
    const attempt: OfflineMediaRecord = {
      ...item,
      state: "PROCESSING",
      attempts: item.attempts + 1,
      lastAttemptAt: new Date().toISOString(),
    };
    await offlineDb.saveMediaOutbox(attempt);
    try {
      const form = new FormData();
      form.append("file", item.blob, item.fileName);
      await apiRequest(item.path, { method: "POST", query: item.query, body: form });
      await offlineDb.removeMediaOutbox(item.id);
      outcome.synced += 1;
    } catch (reason) {
      const error = reason instanceof ApiClientError
        ? reason
        : new ApiClientError(0, { code: "UNKNOWN", message: String(reason) });
      const backoffSeconds = retryBackoffSeconds(attempt.attempts);
      await offlineDb.saveMediaOutbox({
        ...attempt,
        state: "FAILED",
        lastError: error.message,
        nextAttemptAt: new Date(Date.now() + backoffSeconds * 1000).toISOString(),
      });
      outcome.failed += 1;
    }
  }
  window.dispatchEvent(new CustomEvent("aczon:offline-state-change"));
  return outcome;
};

export const processOutbox = (): Promise<SyncOutcome> => {
  if (activeSync) return activeSync;
  activeSync = (async () => {
    // A queued visit must exist before its photo evidence can upload, while
    // completion must run only after the evidence queue has had its turn.
    const commandOutcome = await processOutboxBatch("PRE_MEDIA");
    const mediaOutcome = await processMediaOutboxBatch();
    const completionOutcome = await processOutboxBatch("POST_MEDIA");
    return {
      synced: commandOutcome.synced + mediaOutcome.synced + completionOutcome.synced,
      failed: commandOutcome.failed + mediaOutcome.failed + completionOutcome.failed,
      conflicts: commandOutcome.conflicts + mediaOutcome.conflicts + completionOutcome.conflicts,
    };
  })().finally(() => { activeSync = undefined; });
  return activeSync;
};

export const retryFailed = async () => {
  const [items, mediaItems] = await Promise.all([
    offlineDb.listOutbox(),
    offlineDb.listMediaOutbox(),
  ]);
  await Promise.all([
    ...items
      .filter((item) => item.state === "FAILED")
      .map((item) => offlineDb.saveOutbox({ ...item, state: "QUEUED", nextAttemptAt: undefined })),
    ...mediaItems
      .filter((item) => item.state === "FAILED")
      .map((item) => offlineDb.saveMediaOutbox({ ...item, state: "QUEUED", nextAttemptAt: undefined })),
  ]);
  return processOutbox();
};


export type ConflictResolutionChoice = "KEEP_LOCAL" | "ACCEPT_SERVER" | "MANUAL_MERGE";

export const resolveConflict = async (conflictId: string, choice: ConflictResolutionChoice, mergedPayload?: unknown) => {
  const conflict = await offlineDb.getConflict(conflictId);
  if (!conflict || conflict.resolution !== "UNRESOLVED") throw new Error("Conflict is unavailable or already resolved");
  const draft = await offlineDb.getDraft(conflict.draftId);
  const outboxItems = await offlineDb.listOutbox();
  const outbox = outboxItems.find((item) => item.draftId === conflict.draftId);
  const now = new Date().toISOString();

  if (choice === "ACCEPT_SERVER") {
    if (draft) await offlineDb.saveDraft({ ...draft, payload: conflict.serverPayload ?? draft.payload, state: "SYNCED", updatedAt: now, lastError: undefined, serverSnapshot: conflict.serverPayload });
    if (outbox) await offlineDb.removeOutbox(outbox.id);
  } else {
    if (!outbox || !draft) throw new Error("Conflict no longer has a retryable local command");
    const payload = choice === "MANUAL_MERGE" ? mergedPayload : conflict.localPayload;
    if (choice === "MANUAL_MERGE" && payload === undefined) throw new Error("A merged payload is required");
    const versionedPayload = typeof payload === "object" && payload !== null && conflict.serverVersion !== undefined ? { ...payload, base_row_version: conflict.serverVersion } : payload;
    await offlineDb.saveDraft({ ...draft, payload: versionedPayload, state: "QUEUED", updatedAt: now, lastError: undefined, serverSnapshot: conflict.serverPayload });
    await offlineDb.saveOutbox({ ...outbox, payload: versionedPayload, state: "QUEUED", nextAttemptAt: undefined, lastError: undefined });
  }
  await offlineDb.saveConflict({ ...conflict, resolution: choice });
  window.dispatchEvent(new CustomEvent("aczon:offline-state-change"));
};
